import Slider from "@mui/material/Slider";
import { Dispatch, FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Book, Props, ReadingSession } from "../types";
import { ReadingSessionContainer } from "../ReadingSessionContainer";
import { getPercentage, iOS, isFinished, last } from "../utils";
import { CircularProgressWithLabel } from "../components/CircularProgressWithLabel";
import { GoBack } from "../components/GoBack";
import { FinishedLabel } from "../components/finishedLable";
import { Remove } from "../components/Remove";
import { LineChart } from "../components/LineChart";
import { track } from "@amplitude/analytics-browser";
type TrackerProps = Pick<Props, "setBooks" | "setLastBooksRead"> & {
  book: Book;
  setBook: Dispatch<React.SetStateAction<Book | undefined>>;
  setBooks: Dispatch<React.SetStateAction<Record<string, Book>>>;
};
export const Tracker: FC<TrackerProps> = ({
  book,
  setBook,
  setBooks,
  setLastBooksRead,
}) => {
  const isIOS = iOS();
  const navigate = useNavigate();
  useEffect(() => {
    if (!book) {
      navigate("/home");
      return;
    }
    setMaxPages(book.pages);
  }, [book, navigate]);
  const [maxPages, setMaxPages] = useState<number>(book?.pages || 400);

  const [pages, setPages] = useState<number>(
    last(book.readingSessions)?.page_finish || 0
  );
  const [minPages, setMinPages] = useState<number>(
    last(book.readingSessions)?.page_finish || 0
  );
  const markAsFinished = () => {
    track("tracker: mark as finished", book);
    const session: ReadingSession = {
      page_start: maxPages,
      page_finish: maxPages,
      date: new Date(),
    };
    setBook((b) => {
      if (!b) {
        return b;
      }
      return { ...b, readingSessions: [session] };
    });
    setMinPages(maxPages);
    setPages(maxPages);
  };
  const createSession = () => {
    track("tracker: create new reading session", {
      book,
      minPages,
      pages,
    });
    if (minPages === pages) {
      return;
    }
    setBook((b) => {
      if (!b) {
        return b;
      }
      const arr = [...b.readingSessions];
      const newSession: ReadingSession = {
        page_start: minPages,
        page_finish: pages,
        date: new Date(),
      };
      return { ...b, readingSessions: [...arr, newSession] };
    });
    setMinPages(pages);
  };
  const handleChange = (
    event: any,
    newValue: number | number[],
    buttonNumber?: number
  ) => {
    track("tracker: change pages var", { newValue, buttonNumber, pages });
    if (buttonNumber) {
      setPages((p) => {
        if (p + buttonNumber <= minPages) {
          return minPages;
        }
        if (p >= maxPages && buttonNumber === 1) {
          return maxPages;
        }
        return p + buttonNumber;
      });
    } else {
      if (event.type === "mousedown" && isIOS) {
        return;
      }
      setPages(newValue as number);
    }
  };

  return book ? (
    <div className="container">
      <GoBack />
      {book.readingSessions.length === 0 && (
        <button
          style={{
            position: "absolute",
            top: "5px",
            right: "5px",
          }}
          onClick={markAsFinished}
        >
          mark as finished
        </button>
      )}
      <p className="secctionTitle">Reading progress</p>
      <div className="imageBookTrackerContainer">
        <img
          className="libraryImageBook"
          alt={book.title}
          src={book.imageLink?.thumbnail}
        />
        <div className="circularProsessWithLabel">
          {isFinished(book) ? (
            <FinishedLabel />
          ) : (
            <CircularProgressWithLabel value={getPercentage(book)} />
          )}
        </div>
        {maxPages - minPages !== 0 && (
          <p
            style={{
              margin: "0",
              width: "100px",
              position: "absolute",
              left: "50%",
              transform: "translate( -50%)",
            }}
          >
            {maxPages - minPages} pag left
          </p>
        )}
      </div>

      <div className="pageCounterContainer">
        <button
          onClick={() => {
            track("tracker: lower pages by button", { pages });
            handleChange("", 0, -1);
          }}
          className={pages === minPages ? "disabled" : ""}
          disabled={pages <= minPages}
        >
          -
        </button>
        <div className="trackerMainPageNumber">
          {pages}
          <div className="pags">pags</div>
        </div>
        <button
          onClick={() => {
            track("tracker: increase pages by button", { pages });
            handleChange("", 0, 1);
          }}
          className={pages === maxPages ? "disabled" : ""}
          disabled={pages >= maxPages}
        >
          +
        </button>
      </div>
      <div className="sliderContainer">
        <Slider
          aria-label="pages read"
          value={pages}
          onChange={handleChange}
          min={minPages}
          max={maxPages}
          sx={{ color: "#EA8B8B", height: "10px" }}
        />
        <button
          className={pages === minPages ? "disabled" : ""}
          onClick={createSession}
          disabled={pages <= minPages}
          style={{ fontSize: "1.4rem" }}
        >
          save
        </button>
      </div>
      <p className="secctionTitle">stats</p>
      <LineChart books={{ "1": book }} />
      <p className="secctionTitle">Reading sessions:</p>
      <div
        style={{
          width: "100%",
          padding: "0 5px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {book.readingSessions
          ?.slice()
          .reverse()
          .filter((rs) => rs.page_start !== rs.page_finish)
          .map((row) => (
            <ReadingSessionContainer
              key={"session-" + row.page_start}
              readingSession={row}
            />
          ))}
      </div>
      <Remove
        setBooks={setBooks}
        id={book.id}
        setLastBooksRead={setLastBooksRead}
      />
    </div>
  ) : null;
};
